import type { ReportColumn } from '../models/column.types'
import type { ReportRow } from '../models/report.types'

/**
 * Port of v3 `DataVisible.js:409-429`. A breakdown row carries its creative on the FIRST child in
 * `edges`, not on itself — the parent is the aggregate and has no single creative of its own. v3
 * hoisted the child's media onto the parent so a broken-down table still renders a thumbnail
 * instead of an empty cell (`OrphexReportChart.vue:954` applied it to every response row).
 */
const BREAKDOWN_MEDIA_FIELDS = [
  'media',
  'thumbnail_url',
  'image_url',
  'hotel_image_url',
  'preview_ss_url',
  'multi_media',
  'preview_url',
] as const

/**
 * Image candidates in v3's display order (`OrphexPreviewMedia.vue:57-79`). `media` is last: it is
 * the generic slot `injectMediaFromBreakdown` writes, so a named field always wins over the hoisted
 * copy of itself, and `multi_media` — which has no display branch of its own — still resolves.
 */
const IMAGE_URL_FIELDS = [
  'image_url',
  'thumbnail_url',
  'hotel_image_url',
  'preview_ss_url',
  'product_image_url',
  'media',
] as const

/**
 * Playable candidates in v3 `getVideoField` order (`OrphexChart.js:312-318`) MINUS its `checkVideo`
 * HEAD probes: contract 02 §9 keeps the media layer free of network probing and proxy fallback
 * (backend ask BE-02-01), so an unreachable URL degrades to the broken thumb instead of a request.
 */
const VIDEO_URL_FIELDS = ['video_url', 'preview_url', 'media_url'] as const

/** Card column priority — v3 `OrphexMetricDisplay.vue:147-158`. */
const CARD_METRIC_ORDER: Record<string, number> = {
  cost: 1,
  clicks: 2,
  impressions: 3,
  cpm: 4,
  cpc: 5,
  ctr: 6,
  conversions: 7,
  conversions_value: 8,
  roas: 9,
  cost_per_conversion: 10,
}
const CARD_DIMENSION_ORDER: Record<string, number> = {
  campaign_name: 1,
  ad_name: 2,
  objective: 3,
}

/**
 * Dimensions v3's metric strip never printed: media-URL carriers plus the fields the tile chrome
 * renders itself (`OrphexMetricDisplay.vue:130-140`; `languages` is dropped by the ad-library
 * caller at `OrphexAdLibraryCard.vue:25`).
 */
const HIDDEN_CARD_DIMENSIONS = new Set<string>([
  'ad_last_seen',
  'first_seen',
  'effective_status',
  'marketing_channel',
  'image_url',
  'preview_ss_url',
  'hotel_image_url',
  'thumbnail_url',
  'publisher_platforms',
  'languages',
  'media',
  'multi_media',
])

/** Metrics the tile prints as its own labelled rows rather than in the strip (`:163`). */
const HIDDEN_CARD_METRICS = new Set<string>(['first_seen', 'last_seen', 'active_days'])

export type ReportMediaKind = 'image' | 'video'

/** One renderable creative. `src` is already display-ready; nothing here probes the network. */
export interface ReportMediaAsset {
  src: string
  kind: ReportMediaKind
  /** Poster for a video, and the same URL as `src` for an image. Empty when none resolved. */
  thumbnail: string
  /** Row whose creative produced this asset; carousel chrome and metrics stay on the same slide. */
  source: ReportRow
}

export interface ReportMediaStatusPresentation {
  labelKey: 'active' | 'paused' | 'removed' | null
  fallbackLabel: string
  tone: 'neutral' | 'success' | 'warning' | 'danger'
}

export interface ResolveRowMediaOptions {
  /** The card's declared media kind (`DataVisible`'s `cardMediaType`). Defaults to `image`. */
  kind?: ReportMediaKind
}

/**
 * Resolve the row whose metadata belongs beside the active media asset.
 *
 * v3 treats an `edges` gallery differently from every other media carrier: slide zero is the
 * aggregate ad row and its chronology lives in `ad_*` aliases, while later slides are individual
 * creatives and must never inherit the aggregate chronology. Non-edge galleries present the
 * active child directly. Keeping that distinction here prevents card/header/metric callers from
 * growing subtly different parent-child fallback rules.
 */
export function resolveReportMediaDisplayRow(
  row: ReportRow,
  activeSource: ReportRow | null | undefined,
  activeIndex: number,
  isGallery: boolean,
): ReportRow {
  if (row.edges) {
    if (activeIndex === 0) {
      return {
        ...row,
        first_seen: row['ad_first_seen'] ?? null,
        last_seen: row['ad_last_seen'] ?? null,
        active_days: row['ad_active_days'] ?? null,
      }
    }

    return {
      ...row,
      ...(activeSource ?? {}),
      first_seen: activeSource?.['first_seen'] ?? null,
      last_seen: activeSource?.['last_seen'] ?? null,
      active_days: activeSource?.['active_days'] ?? null,
    }
  }

  if (isGallery && activeSource) return activeSource
  return { ...row, ...(activeSource ?? {}) }
}

/**
 * v3 required `length > 4` on every media URL (`OrphexPreviewMedia.vue:60-79`, `getVideoField`)
 * because the controller writes stubs — `''`, `'-'`, and the literal string `'nan'` — into these
 * columns. The length rule subsumes all three, so it is kept as the single check.
 */
function usableUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 4 ? trimmed : null
}

function pickUrl(row: ReportRow, fields: readonly string[]): string | null {
  for (const field of fields) {
    const url = usableUrl(row[field])
    if (url) return url
  }
  return null
}

/**
 * The child rows a breakdown carries its creatives on. v3 read `imgArr || breakdown_values ||
 * edges`; `imgArr` was manufactured by `OrphexReportMediaCard.getData()` unshifting the parent into
 * its own children, a workaround for the missing hoist that `injectMediaFromBreakdown` now does.
 */
function mediaChildren(row: ReportRow | null | undefined): ReportRow[] {
  return row?.edges ?? row?.breakdown_values ?? []
}

/**
 * Hoist the first breakdown child's media fields onto its parent row.
 *
 * `media` is deliberately overwritten by each truthy field in turn, so it ends up holding the LAST
 * one present in `BREAKDOWN_MEDIA_FIELDS` order — v3 `:422-427` behaves exactly this way, and the
 * renderers read `media` as the generic slot. Preserved verbatim: reordering the list silently
 * changes which creative a broken-down row shows.
 *
 * `edges` wins over `breakdown_values` when a row carries both, so every pre-existing caller keeps
 * byte-identical output; `breakdown_values` is the second carrier the card view needs, and v3's own
 * tiles always treated the two as interchangeable (`OrphexMediaCard.vue:129`).
 */
export function injectMediaFromBreakdown(row: ReportRow): ReportRow {
  const first = mediaChildren(row)[0]
  if (!first) return row

  const out: ReportRow = { ...row }
  for (const field of BREAKDOWN_MEDIA_FIELDS) {
    if (first[field]) {
      out['media'] = first[field]
      out[field] = first[field]
    }
  }
  return out
}

function toAsset(row: ReportRow, kind: ReportMediaKind): ReportMediaAsset | null {
  const thumbnail = pickUrl(row, IMAGE_URL_FIELDS)
  if (kind === 'video') {
    // Falling back to the still keeps a poster-only creative visible; module 02's PreviewVideo
    // renders a non-playable URL as an image rather than an empty frame.
    const src = pickUrl(row, VIDEO_URL_FIELDS) ?? thumbnail
    return src ? { src, kind: 'video', thumbnail: thumbnail ?? '', source: row } : null
  }
  return thumbnail ? { src: thumbnail, kind: 'image', thumbnail, source: row } : null
}

/** v3 status chip normalization from `OrphexMediaCard`, kept beside the media recipe. */
export function presentReportMediaStatus(value: unknown): ReportMediaStatusPresentation | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  const status = value.trim().toUpperCase()
  if (['ACTIVE', 'CAMPAIGN_ACTIVE', 'ADSET_ACTIVE', 'AD_ACTIVE'].includes(status)) {
    return { labelKey: 'active', fallbackLabel: '', tone: 'success' }
  }
  if (['PAUSED', 'CAMPAIGN_PAUSED', 'ADSET_PAUSED', 'AD_PAUSED'].includes(status)) {
    return { labelKey: 'paused', fallbackLabel: '', tone: 'warning' }
  }
  if (['REMOVED', 'DELETED', 'ARCHIVED'].includes(status)) {
    return { labelKey: 'removed', fallbackLabel: '', tone: 'danger' }
  }
  return {
    labelKey: null,
    fallbackLabel: status
      .toLowerCase()
      .replaceAll('_', ' ')
      .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase()),
    tone: 'neutral',
  }
}

/**
 * The module's single media-resolution path (ORP-18651 AC4): every card asset comes from here, and
 * `injectMediaFromBreakdown` is applied INSIDE it so callers holding un-normalized rows — the
 * summary carousel reads `useReportData.rows` directly — resolve identically to the report grid.
 *
 * Two or more children carrying a creative become a gallery, matching v3's `isSwiperActive`
 * (`OrphexMediaCard.vue:128-138`); anything less collapses to the parent's own hoisted media.
 */
export function resolveRowMedia(
  row: ReportRow | null | undefined,
  options: ResolveRowMediaOptions = {},
): ReportMediaAsset[] {
  if (!row) return []
  const kind = options.kind ?? 'image'
  const gallery = mediaChildren(row)
    .map((child) => toAsset(child, kind))
    .filter((asset): asset is ReportMediaAsset => asset !== null)
  if (gallery.length > 1) return gallery

  const own = toAsset(injectMediaFromBreakdown(row), kind)
  return own ? [own] : []
}

function orderBy(columns: readonly ReportColumn[], order: Record<string, number>): ReportColumn[] {
  return [...columns].sort(
    (a, b) =>
      (order[a.field] ?? Number.POSITIVE_INFINITY) - (order[b.field] ?? Number.POSITIVE_INFINITY),
  )
}

/**
 * Columns a media tile prints, in v3's order. `OrphexMetricDisplay.vue:126-163` sorts EVERY tile by
 * the same two maps — campaign/ad/objective for dimensions, cost→cost_per_conversion for metrics —
 * and `OrphexAdLibraryCard.vue:67-98` reuses them, so the order is not variant-specific.
 */
export function orderCardColumns(
  columns: readonly ReportColumn[],
  role: 'dimension' | 'metric',
): ReportColumn[] {
  const hidden = role === 'dimension' ? HIDDEN_CARD_DIMENSIONS : HIDDEN_CARD_METRICS
  const visible = columns.filter(
    (column) =>
      !hidden.has(column.field) &&
      !column.invisible &&
      !(role === 'dimension' && (column.isImage || column.field.endsWith('_url'))),
  )
  return orderBy(visible, role === 'dimension' ? CARD_DIMENSION_ORDER : CARD_METRIC_ORDER)
}

/** Add report-specific media fields without mutating controller-owned rows. */
export function normalizeReportTableRows(
  rows: readonly ReportRow[],
  reportId?: number | null,
): ReportRow[] {
  return rows.map((row) => {
    const withMedia = injectMediaFromBreakdown(row)
    if (reportId !== 27) return { ...withMedia }
    const videoId = String(withMedia['video_id'] ?? '')
    return {
      ...withMedia,
      youtube_video_url: `https://www.youtube.com/watch?v=${videoId}`,
      youtube_thumbnail_url: `https://img.youtube.com/vi/${videoId}/0.jpg`,
    }
  })
}
